import * as React from 'react';
import {
  Expressions,
  TypeCheckerState,
  TypeImplementation,
  Types,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {TBoolean} from '@packages/systems/plugins-utils/Boolean';
import {
  EList,
  type Expression,
  TList,
  TRecord,
  TTypeReference,
} from '@packages/systems/wfdl-ast';
import {
  ContextualExpressions,
  type ContextualExpression,
  getAnn,
  stripAnn,
  stripAll,
} from '@packages/utilities/contextual-expression';
import {
  AddIcon,
  Button,
  Card,
  Divider,
  Text,
  DeleteIcon,
  IconButton,
  Select,
  View,
  HStack,
  VStack,
  NestIcon,
  RowItem,
  DragItemWrapper,
  DragItemHandle,
} from '@packages/systems/spring';
import {ExpressionEditor} from '@packages/systems/ExpressionEditor';
import {
  ExpressionBinding,
  ExpressionEditorDispatch,
  type ExpressionBindMetaPayload,
  extendExpressionEditorActionAtPath,
  updateExpressionAction,
  updateExpressionAtAction,
  updateTypeSystem,
} from '@packages/systems/ExpressionEditor-utils';
import {
  findAvailableBindings,
  hasAvailableBindings,
  emptyAvailableBindings,
} from '@packages/systems/wfdl-findBindings';
import {usePlugins} from '@packages/utilities/platform';
import {emotionStyled, colors} from '@packages/utilities/styled';
import {
  buildNewConditionalRuleExpression,
  ConditionalMode,
  ConditionalModes,
  createConditionalWithMode,
  getConditionalMode,
  getNewGroupDefaultOutputValue,
  getSimpleConditionalAutoAssignedValue,
} from '@packages/systems/plugins-utils/Conditionals';
import {RuleConditions} from './RuleConditions';
import {GetPathForDispatch} from './types';
import {bindChangedAt} from './utils';
import {moveElement, deleteAt} from '@packages/utilities/array';
import {conditionGroupCreatedAt} from '@packages/systems/conditionals';
import {Dispatch} from '@packages/systems/flux';
import {
  pointerWithin,
  DndContext,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
  type DragEndEvent,
  useDndContext,
  type Active,
  type Over,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {useDispatch} from '@packages/systems/designer/flux';

const FlatCard = emotionStyled(Card)({
  border: '1px solid $border1',
  background: '$backgroundSecondary',
});

const FallbackValueCard = emotionStyled(FlatCard)({
  marginTop: '$2',
});

const DeleteButtonWrapper = emotionStyled('div')({
  visibility: 'hidden',
  gridColumn: 'delete-button',
});

const GroupHeader = emotionStyled('div')({
  display: 'grid',
  gridColumnGap: '0',
  gridTemplateColumns:
    '[drag-handle] 12px [if] 16px [any-all] 42px $1 [conditions-are-true] 1fr [delete-button] 24px $1',
  alignItems: 'center',
  paddingTop: '$2',
  paddingBottom: '$2',

  [`&:hover ${DeleteButtonWrapper}`]: {
    visibility: 'visible',
  },
});

const fallbackPath: ExpressionPath = [{in: 'Record', at: 'fallback'}];
const firstOutputPath: ExpressionPath = [
  {in: 'Record', at: 'rules'},
  {in: 'List', index: 0},
  {in: 'Record', at: 'output'},
];

const handleSimpleConditionalOutputAndFallbackValueAutoAssignment = (
  action: TypeImplementation.ExpressionEditorAction,
  firstGroupOutputValue: ContextualExpression,
  fallbackValue: ContextualExpression,
  fullExpr: ContextualExpression
): TypeImplementation.ExpressionEditorAction => {
  const targetsFirstGroupOutputValue = Expressions.pathsEqual(
    action.key,
    firstOutputPath
  );

  if (
    targetsFirstGroupOutputValue &&
    !ContextualExpressions.isBinding(stripAnn(fallbackValue))
  ) {
    const fallbackType = getAnn(fallbackValue).type;
    if (action.type === 'EXPRESSION_ACTION') {
      const newFirstGroupOutputValueExpr = action.payload.newExpression;
      const autoAssignedFallbackValue = fallbackType
        ? getSimpleConditionalAutoAssignedValue(
            fallbackType,
            newFirstGroupOutputValueExpr
          )
        : null;

      if (autoAssignedFallbackValue) {
        return updateExpressionAction(
          Expressions.setIn(
            Expressions.setIn(
              stripAll(fullExpr),
              firstOutputPath,
              newFirstGroupOutputValueExpr
            ),
            fallbackPath,
            autoAssignedFallbackValue
          )
        );
      }
    } else if (
      action.type === 'EXPRESSION_BIND_CHANGED' &&
      action.binding === null
    ) {
      const autoAssignedFirstGroupOutputValue = fallbackType
        ? getSimpleConditionalAutoAssignedValue(
            fallbackType,
            stripAll(fallbackValue)
          )
        : null;

      if (autoAssignedFirstGroupOutputValue) {
        return updateExpressionAtAction(
          autoAssignedFirstGroupOutputValue,
          firstOutputPath
        );
      }
    }
  }

  const targetsFallbackValue = Expressions.pathsEqual(action.key, fallbackPath);

  if (
    targetsFallbackValue &&
    !ContextualExpressions.isBinding(stripAnn(firstGroupOutputValue))
  ) {
    const outputType = getAnn(firstGroupOutputValue).type;

    if (action.type === 'EXPRESSION_ACTION') {
      const newFallbackValueExpr = action.payload.newExpression;
      const autoAssignedOutputValue = outputType
        ? getSimpleConditionalAutoAssignedValue(
            outputType,
            newFallbackValueExpr
          )
        : null;

      if (autoAssignedOutputValue) {
        return updateExpressionAction(
          Expressions.setIn(
            Expressions.setIn(
              stripAll(fullExpr),
              fallbackPath,
              newFallbackValueExpr
            ),
            firstOutputPath,
            autoAssignedOutputValue
          )
        );
      }
    } else if (
      action.type === 'EXPRESSION_BIND_CHANGED' &&
      action.binding === null
    ) {
      const autoAssignedFallbackValue = outputType
        ? getSimpleConditionalAutoAssignedValue(
            outputType,
            stripAll(firstGroupOutputValue)
          )
        : null;

      if (autoAssignedFallbackValue) {
        return updateExpressionAtAction(
          autoAssignedFallbackValue,
          fallbackPath
        );
      }
    }
  }

  // Default to normal dispatch if some condition still fails in the above logic
  return action;
};

const CONDITIONAL_MODE_OPTIONS = [
  {label: ConditionalModes.All, value: ConditionalModes.All},
  {label: ConditionalModes.Any, value: ConditionalModes.Any},
] as const;

export const ConditionalConfiguration = TypeImplementation.Alias({
  params: ['x'],
  type: TRecord({
    rules: TList(
      TRecord({
        condition: TBoolean,
        output: TTypeReference('x'),
      })
    ),
    fallback: TTypeReference('x'),
  }),
  render: ConditionalConfigurationRender,
});

function ConditionalConfigurationRender({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
}) {
  const wfDispatch = useDispatch();
  const {expressionPath} = getAnn(expr).env;
  const getPathForDispatch: GetPathForDispatch = React.useCallback(
    function getPathForDispatch(nestedExpr: ContextualExpression) {
      return getAnn(nestedExpr).env.expressionPath.slice(expressionPath.length);
    },
    [expressionPath]
  );

  const deconstructed = deconstructConfiguration(expr);

  const deleteRule = React.useCallback(
    (ruleIndex: number) => {
      if (!deconstructed) return;
      const {rulesExpr} = deconstructed;
      const strippedRulesExpr = stripAll(rulesExpr);
      const rulesList = Expressions.getList(strippedRulesExpr);
      if (!rulesList) return;

      const newRulesList = deleteAt(rulesList, ruleIndex);
      const newRulesExpr = EList(newRulesList);
      const path = getPathForDispatch(rulesExpr);
      dispatch(updateExpressionAtAction(newRulesExpr, path));
    },
    [deconstructed, dispatch, getPathForDispatch]
  );

  const handleModeChange = React.useCallback(
    (ruleCondition: ContextualExpression, newValue: ConditionalMode) => {
      const newConditionExpr = createConditionalWithMode(
        ruleCondition,
        newValue
      );
      const path = getPathForDispatch(ruleCondition);
      dispatch(updateExpressionAtAction(newConditionExpr, path));
    },
    [dispatch, getPathForDispatch]
  );

  const groupListMoved = React.useCallback(
    (oldIndex: number, newIndex: number) => {
      if (!deconstructed) return;
      const {rulesExpr} = deconstructed;
      // The list of rules is located at expr.rules, and is the `rulesExpr`
      // from `deconstructConfiguration`.
      const strippedRules = stripAll(rulesExpr);
      const newRulesExpr = EList(
        moveElement(
          Expressions.getList(strippedRules) || [],
          oldIndex,
          newIndex
        )
      );
      const path = getPathForDispatch(rulesExpr);
      dispatch(updateExpressionAtAction(newRulesExpr, path));
    },
    [deconstructed, dispatch, getPathForDispatch]
  );

  if (!deconstructed) {
    return (
      <Text font="helpText" colorVariant="warning">
        Invalid conditional configuration!
      </Text>
    );
  }

  const {rulesExpr, rules, fallbackValue} = deconstructed;

  const outputAndFallbackDispatch = (
    action: TypeImplementation.ExpressionEditorAction
  ) => {
    const firstGroup = rules[0];
    const firstGroupOutputValue = firstGroup?.ruleOutputValue;
    if (rules.length === 1 && firstGroupOutputValue) {
      action = handleSimpleConditionalOutputAndFallbackValueAutoAssignment(
        action,
        firstGroupOutputValue,
        fallbackValue,
        expr
      );
    }
    dispatch(action);
  };

  return (
    <>
      <Rules
        items={rules}
        getPathForDispatch={getPathForDispatch}
        dispatch={dispatch}
        groupListMoved={groupListMoved}
        deleteRule={deleteRule}
        changeMode={handleModeChange}
        outputAndFallbackDispatch={outputAndFallbackDispatch}
      />
      <NewGroupButton
        groupsExpr={rulesExpr}
        dispatch={dispatch}
        getPathForDispatch={getPathForDispatch}
        wfDispatch={wfDispatch}
        fallbackValue={fallbackValue}
        conditionalExpr={expr}
      />
      <FallbackValueCard data-automation-id="conditional-fallback" padding="$3">
        <Text as="div" font="body" marginBottom="$3">
          Else
        </Text>
        <OutputValue
          expr={fallbackValue}
          dispatch={outputAndFallbackDispatch}
          getPathForDispatch={getPathForDispatch}
          automationId="conditional-fallback-output-wrapper"
        />
      </FallbackValueCard>
    </>
  );
}

const OutputValueWrapper = emotionStyled('div')({
  display: 'grid',
  gridColumnGap: '0',
  gridTemplateColumns: '[icon] 16px 2px [label] 1fr [settings] 188px',
  alignItems: 'start',
  // Matches input top-padding so labels align with single-line input content.
  '> :not(:last-child)': {
    marginTop: '$1',
  },
});

const OutputValueLabel = emotionStyled(HStack)({
  gridColumn: 'label',
  gap: 4,
});

function OutputValue({
  expr,
  dispatch,
  getPathForDispatch,
  automationId,
}: {
  expr: ContextualExpression;
  dispatch: ExpressionEditorDispatch;
  getPathForDispatch: GetPathForDispatch;
  automationId?: string;
}) {
  const {type} = getAnn(expr);
  const label = type ? Types.getLabel(type) : 'Value';
  const path = getPathForDispatch(expr);
  const valueDispatch = extendExpressionEditorActionAtPath(dispatch)(path);

  // We want to show the binding dot only when there are available bindings.
  // TODO (STRUCT-2531): Explore if conditionals availability could be a
  // part of availableBindings.
  const plugins = usePlugins();
  const [rootCheckerState] = React.useContext(TypeCheckerState);
  const bindings = React.useMemo(
    () =>
      rootCheckerState && type
        ? findAvailableBindings(expr, rootCheckerState, plugins)
        : emptyAvailableBindings,
    [expr, rootCheckerState, plugins, type]
  );
  const hasBindings = hasAvailableBindings(bindings);

  const dataTypeImpl = type ? Types.getImplementation(type, plugins) : null;
  const bindUIVariant = dataTypeImpl
    ? TypeImplementation.getBindUIVariant(dataTypeImpl)
    : null;
  const isBound = ContextualExpressions.isBinding(stripAnn(expr));
  // We are hiding the label to prevent the label from being rendered twice
  // but we still need to show the binding dot to indicate that the value is bindable
  // in the cases where the bindUIVariant is 'label'. Thus we need to show the binding dot manually.
  const needsExternalBinding =
    hasBindings && !isBound && bindUIVariant === 'label';

  const updatedExpr = React.useMemo(
    () =>
      updateTypeSystem(expr, (system) => ({
        ...system,
        labelVariant: 'hidden',
        private: false,
        isBindable: hasBindings,
      })),
    [expr, hasBindings]
  );

  const onBindingChange = React.useCallback(
    (binding: Expression | null, meta: ExpressionBindMetaPayload) => {
      valueDispatch({
        type: 'EXPRESSION_BIND_CHANGED',
        binding,
        meta,
        key: [],
      });
    },
    [valueDispatch]
  );

  return (
    <OutputValueWrapper data-automation-id={automationId}>
      <NestIcon colorVariant="secondary" style={{gridColumn: 'icon'}} />
      {needsExternalBinding ? (
        <OutputValueLabel>
          <ExpressionBinding
            expression={updatedExpr}
            onChange={onBindingChange}
            bindUIVariant="label"
            automationLabel={label || 'Value'}
          >
            <Text font="body" colorVariant="secondary">
              {label}
            </Text>
          </ExpressionBinding>
        </OutputValueLabel>
      ) : (
        <Text font="body" colorVariant="secondary" gridColumn="label">
          {label}
        </Text>
      )}
      <View gridColumn="settings">
        <ExpressionEditor expression={updatedExpr} dispatch={valueDispatch} />
      </View>
    </OutputValueWrapper>
  );
}

// creates a button with a shadow that extends outwards 24px left and right
const ButtonWithShadow = emotionStyled(Button)({
  boxShadow: `16px 0px 8px ${colors.backgroundSecondary}, -16px 0px 8px ${colors.backgroundSecondary}`,
  // this ensures the shadow is only visible on the left and right
  clipPath: 'inset(0 -24px 0 -24px)',
});

function NewGroupButton({
  groupsExpr,
  dispatch,
  getPathForDispatch,
  wfDispatch,
  fallbackValue,
  conditionalExpr,
}: {
  groupsExpr: ContextualExpression;
  dispatch: ExpressionEditorDispatch;
  getPathForDispatch: GetPathForDispatch;
  wfDispatch: Dispatch;
  fallbackValue: ContextualExpression;
  conditionalExpr: ContextualExpression;
}) {
  const [_, rootContextualExpr] = React.useContext(TypeCheckerState);
  const addRule = React.useCallback(() => {
    const strippedGroupsExpr = stripAll(groupsExpr);
    const groups = Expressions.getList(strippedGroupsExpr);
    if (!groups) return;
    const settingExpr = rootContextualExpr
      ? ContextualExpressions.getIn(
          rootContextualExpr,
          getAnn(conditionalExpr).env.expressionPath.slice(0, -1)
        )
      : null;
    const type = settingExpr ? getAnn(settingExpr).type : null;
    const newOutputValue = getNewGroupDefaultOutputValue(type, fallbackValue);
    const newRulesExpr = EList([
      ...groups,
      buildNewConditionalRuleExpression(newOutputValue),
    ]);
    const path = getPathForDispatch(groupsExpr);
    dispatch(bindChangedAt(newRulesExpr, getAnn(groupsExpr).type, path));
    // We dispatch here also an UI only action to power the auto open functionality
    const fullPath = getAnn(groupsExpr).env.expressionPath;
    const newGroupPath: ExpressionPath = [
      ...fullPath,
      {in: 'List', index: groups.length},
    ];
    wfDispatch(conditionGroupCreatedAt({expressionPath: newGroupPath}));
  }, [
    groupsExpr,
    dispatch,
    getPathForDispatch,
    wfDispatch,
    fallbackValue,
    conditionalExpr,
    rootContextualExpr,
  ]);

  return (
    <HStack justify="center" marginTop="$2">
      <VStack>
        <Divider spacing={3} />
      </VStack>
      <ButtonWithShadow
        onClick={addRule}
        variant="ghost"
        colorVariant="secondary"
        density="compact"
        prefix={AddIcon}
        data-automation-id="new-group-button"
      >
        New group
      </ButtonWithShadow>
      <VStack>
        <Divider spacing={3} />
      </VStack>
    </HStack>
  );
}

type DeconstructedConditionalRule = {
  ruleCondition: ContextualExpression;
  ruleOutputValue: ContextualExpression;
};

type DeconstructedConditional = {
  rulesExpr: ContextualExpression;
  rules: DeconstructedConditionalRule[];
  fallbackValue: ContextualExpression;
};

export function deconstructConfiguration(
  expr: ContextualExpression
): DeconstructedConditional | null {
  const recordResult = ContextualExpressions.getRecord(stripAnn(expr));
  if (recordResult) {
    const rulesExpr = recordResult.rules;
    const fallbackValue = recordResult.fallback;

    if (rulesExpr && fallbackValue) {
      const rulesList = ContextualExpressions.getList(stripAnn(rulesExpr));

      if (rulesList) {
        const rules: DeconstructedConditionalRule[] = [];

        for (const ruleExpr of rulesList) {
          const ruleResult = ContextualExpressions.getRecord(
            stripAnn(ruleExpr)
          );

          if (!ruleResult) {
            return null;
          }

          const ruleCondition = ruleResult.condition;
          const ruleOutputValue = ruleResult.output;

          if (!ruleCondition || !ruleOutputValue) {
            return null;
          }

          rules.push({ruleCondition, ruleOutputValue});
        }

        return {rulesExpr, rules, fallbackValue};
      }
    }
  }
  return null;
}

const DraggableRow = ({
  rowIndex,
  item,
  dispatch,
  getPathForDispatch,
  deleteRule,
  changeMode,
  canDeleteGroup,
  outputAndFallbackDispatch,
  dragEnabled,
}: {
  rowIndex: number;
  item: DeconstructedConditionalRule;
  dispatch: ExpressionEditorDispatch;
  getPathForDispatch: GetPathForDispatch;
  deleteRule: (index: number) => void;
  changeMode: (
    ruleCondition: ContextualExpression,
    newValue: ConditionalMode
  ) => void;
  canDeleteGroup: boolean;
  outputAndFallbackDispatch: ExpressionEditorDispatch;
  dragEnabled: boolean;
}) => {
  const itemId = `conditional-group-${rowIndex}`;
  const {isDragging, attributes, listeners, setNodeRef, setActivatorNodeRef} =
    useSortable({
      id: itemId,
    });

  const dragProps = {
    ...attributes,
    ...listeners,
  };

  const {ruleCondition, ruleOutputValue} = item;

  return (
    <DragItemWrapper
      isDragging={isDragging}
      ref={setNodeRef}
      data-automation-id={`draggable-conditional-group-${rowIndex + 1}`}
    >
      <RowItem>
        <React.Fragment key={`conditional-rule-${rowIndex}`}>
          <FlatCard
            isSeamless
            data-automation-id={`conditional-group-${rowIndex + 1}`}
          >
            <GroupHeader data-automation-id="conditional-group-header">
              {dragEnabled ? (
                <View style={{position: 'relative'}} gridColumn="drag-handle">
                  <DragItemHandle {...dragProps} ref={setActivatorNodeRef} />
                </View>
              ) : null}
              <Text font="body" gridColumn="if">
                If
              </Text>
              <View gridColumn="any-all">
                <Select
                  value={getConditionalMode(ruleCondition)}
                  onChange={(newValue) => changeMode(ruleCondition, newValue)}
                  options={CONDITIONAL_MODE_OPTIONS}
                  variant="outline"
                  data-automation-id={`conditional-group-${rowIndex + 1}-mode-select`}
                />
              </View>
              <Text font="body" gridColumn="conditions-are-true">
                conditions in this group are true
              </Text>
              {canDeleteGroup && (
                <DeleteButtonWrapper>
                  <IconButton
                    Icon={DeleteIcon}
                    label="Delete group"
                    variant="ghost"
                    onClick={() => deleteRule(rowIndex)}
                    data-automation-id={`conditional-group-${rowIndex + 1}-delete`}
                  />
                </DeleteButtonWrapper>
              )}
            </GroupHeader>
            <RuleConditions
              expr={ruleCondition}
              dispatch={dispatch}
              getPathForDispatch={getPathForDispatch}
            />
            <View
              data-automation-id="conditional-group-output-value"
              paddingLeft="$3"
              paddingRight="$3"
              paddingBottom="$3"
            >
              <Divider spacing={2} />
              <OutputValue
                expr={ruleOutputValue}
                dispatch={outputAndFallbackDispatch}
                getPathForDispatch={getPathForDispatch}
                automationId="conditional-rule-output-wrapper"
              />
            </View>
          </FlatCard>
        </React.Fragment>
      </RowItem>
    </DragItemWrapper>
  );
};
const DropIndicatorView = emotionStyled(View)(
  {
    height: 3,
    background: '$blueBackground',
    color: '$textPrimary',
    position: 'fixed',
    top: 0,
    display: 'grid',
    placeItems: 'center',
    pointerEvents: 'none',
  },
  {
    variants: {
      variant: {
        default: {},
        error: {
          background: '$redBackground',
        },
      },
    },
  }
);

const getDragGroupIdx = (item: Active | Over): number => {
  const stringId = String(item.id);
  // Note that this assumes the id remains in
  return Number(stringId.split('-').pop());
};
const getDropRelation = (active: Active, over: Over): 'before' | 'after' => {
  const activeIdx = getDragGroupIdx(active);
  const overIdx = getDragGroupIdx(over);
  return activeIdx < overIdx ? 'after' : 'before';
};

const DropIndicator = () => {
  const dndContextResult = useDndContext();
  const {active, over, droppableRects} = dndContextResult;

  const style = React.useMemo(() => {
    let dropData = null;
    if (over != null && active != null) {
      dropData = {
        dropLocation: over.id,
        relation: getDropRelation(active, over),
      };
    }

    if (dropData == null) return undefined;
    const {dropLocation, relation} = dropData;
    const rect = droppableRects.get(dropLocation);
    if (rect == null) return undefined;
    const top = relation === 'before' ? rect.top : rect.bottom;
    return {
      transform: `translateY(${top}px)`,
      // Because we use fixed positioning, we need to use the computed width for
      // our indicator's width
      width: rect.width,
    };
  }, [droppableRects, over, active]);

  const indicator = React.useMemo(
    () => <DropIndicatorView variant="default" style={style} />,
    [style]
  );

  return active != null && indicator;
};

const Rules = ({
  items,
  getPathForDispatch,
  dispatch,
  groupListMoved,
  deleteRule,
  changeMode,
  outputAndFallbackDispatch,
}: {
  items: DeconstructedConditionalRule[];
  getPathForDispatch: GetPathForDispatch;
  dispatch: ExpressionEditorDispatch;
  groupListMoved: (oldIndex: number, newIndex: number) => void;
  deleteRule: (index: number) => void;
  changeMode: (
    ruleCondition: ContextualExpression,
    newValue: ConditionalMode
  ) => void;
  outputAndFallbackDispatch: ExpressionEditorDispatch;
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // We rely on the item's index in the array as a way to control
  // drop indicator location. If this format changes remember to also
  // update getDragGroupIdx
  const itemIds = React.useMemo(
    () => items.map((_item, idx) => `conditional-group-${idx}`),
    [items]
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const {active, over} = event;

    if (over?.id && active.id !== over.id) {
      const oldIndex = itemIds.indexOf(String(active.id));
      const newIndex = itemIds.indexOf(String(over.id));
      groupListMoved(oldIndex, newIndex);
    }
  };

  return (
    <DndContext
      collisionDetection={pointerWithin}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <View>
          <VStack spacing={0}>
            {items.map((item, index) => (
              <React.Fragment key={`conditional-rule-${index}`}>
                <DraggableRow
                  key={index}
                  rowIndex={index}
                  item={item}
                  dispatch={dispatch}
                  getPathForDispatch={getPathForDispatch}
                  canDeleteGroup={items.length > 1}
                  deleteRule={deleteRule}
                  changeMode={changeMode}
                  outputAndFallbackDispatch={outputAndFallbackDispatch}
                  dragEnabled={items.length > 1}
                />
                {index < items.length - 1 && (
                  <Text
                    as="div"
                    font="body"
                    marginTop="$1"
                    marginBottom="$1"
                    textAlign="center"
                  >
                    or
                  </Text>
                )}
              </React.Fragment>
            ))}
          </VStack>
        </View>
      </SortableContext>
      <DropIndicator />
    </DndContext>
  );
};
